import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { RedlineProvider } from '@libs/redline';

import { AuthenticationProvider } from '@libs/acp/contexts/Authentication';
import { PartnerAuthenticationProvider } from '@libs/acp/contexts/PartnerAuthentication';

import { UserProvider } from '@common/contexts/UserContext/context';
import { WebBridgeProvider } from '@libs/app-bridge';
import { identityApi } from '@common/services/identity.api';
import { DogxProvider } from '@libs/dogx';
import { SHProvider } from '@libs/creditkit';
import { AdsProvider } from '@libs/ads';

import App from './App';
import getRedlineAppName from '@common/utils/getRedlineAppName';
import { createRedline } from 'redline-client-sdk';
import handlePreloadError from '@libs/utils/helpers/handlePreloadError';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, persistConfig } from '@common/services/queryClient';

window.addEventListener('vite:preloadError', handlePreloadError);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const rl = createRedline({
  writeKey: import.meta.env.VITE_CP_REDLINE_WRITE_KEY,
  mode: import.meta.env.VITE_ENV,
  logs: false,
  appName: getRedlineAppName(),
  appVersion: import.meta.env.VITE_DEPLOY_VERSION,
});

const redlineAppName = getRedlineAppName();

const isRenderInPartner = window !== window.parent;

root.render(
  <PersistQueryClientProvider client={queryClient} persistOptions={{ ...persistConfig }}>
    <RedlineProvider instance={rl} appName={redlineAppName}>
      <WebBridgeProvider apiInstance={identityApi} brand="cp">
        {isRenderInPartner ? (
          <BrowserRouter>
            <PartnerAuthenticationProvider>
              <App />
            </PartnerAuthenticationProvider>
          </BrowserRouter>
        ) : (
          <DogxProvider>
            <SHProvider
              appName="cp"
              mode={import.meta.env.VITE_ENV}
              shKey={import.meta.env.VITE_SORTING_HAT_API_KEY}
              baseURL={import.meta.env.VITE_SORTING_CREDIT_API_URL}
              identifier="userDocument"
            >
              <BrowserRouter>
                <AuthenticationProvider>
                  <UserProvider>
                    <AdsProvider>
                      <App />
                    </AdsProvider>
                  </UserProvider>
                </AuthenticationProvider>
              </BrowserRouter>
            </SHProvider>
          </DogxProvider>
        )}
      </WebBridgeProvider>
    </RedlineProvider>
  </PersistQueryClientProvider>
);
